/**
 * Footer Sidebar Functionality
 * Handles opening/closing of country and language selection sidebars
 */

class FooterSidebar {
  constructor() {
    this.activeSidebar = null;
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupEventListeners());
    } else {
      this.setupEventListeners();
    }
  }

  setupEventListeners() {
    
    // Trigger buttons
    const triggers = document.querySelectorAll('.footer-sidebar-trigger');
    
    triggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const sidebarType = trigger.dataset.sidebar;
        this.openSidebar(sidebarType);
      });
    });

    // Close buttons
    const closeButtons = document.querySelectorAll('.footer-sidebar__close');
    closeButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const sidebarType = button.dataset.sidebarClose;
        this.closeSidebar(sidebarType);
      });
    });

    // Overlay clicks
    const overlays = document.querySelectorAll('.cart-drawer__overlay');
    overlays.forEach(overlay => {
      overlay.addEventListener('click', (e) => {
        e.preventDefault();
        const sidebarType = overlay.dataset.sidebarOverlay;
        this.closeSidebar(sidebarType);
      });
    });

    // Option selection and form submission
    this.setupOptionSelection();

    // Search functionality
    this.setupSearch();

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.activeSidebar) {
        this.closeSidebar(this.activeSidebar);
      }
    });
  }

  openSidebar(type) {
    
    // Close any open sidebar first
    if (this.activeSidebar && this.activeSidebar !== type) {
      this.closeSidebar(this.activeSidebar);
    }

    const sidebar = document.querySelector(`.footer-sidebar[data-sidebar="${type}"]`);
    
    if (sidebar) {
      sidebar.classList.add('active');
      this.activeSidebar = type;
      
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
      
      // Focus trap
      this.trapFocus(sidebar);
    } else {
      console.error('Sidebar not found for type:', type);
    }
  }

  closeSidebar(type) {
    const sidebar = document.querySelector(`.footer-sidebar[data-sidebar="${type}"]`);
    if (sidebar) {
      sidebar.classList.remove('active');
      sidebar.classList.add('closing');
      
      setTimeout(() => {
        sidebar.classList.remove('closing');
      }, 300);
      
      this.activeSidebar = null;
      
      // Restore body scroll
      document.body.style.overflow = '';
    }
  }

  setupOptionSelection() {
    // Country selection
    const countryForm = document.querySelector('#FooterCountryFormSidebar');
    if (!countryForm) return;
    
    countryForm.addEventListener('submit', (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
        return false;
    }, true);
    
    const countryOptions = document.querySelectorAll('#country-sidebar .footer-sidebar__option input[type="radio"]');
    
    let isSubmitting = false;
    countryOptions.forEach(option => {
      option.addEventListener('change', (e) => {
        if (!e.target.checked) return;

        const newCountryCode = e.target.value;
        const currentUrl = new URL(window.location.href);
        const currentCountry = currentUrl.searchParams.get('country');
        
        // Only submit if country is actually changing
        if (currentCountry === newCountryCode) {
          return;
        }
        
        // Update active state
        this.updateActiveOption(e.target, 'country');
        
        // Build clean return URL without proxy parameters
        let returnPath = currentUrl.pathname;
        if (!returnPath || returnPath === '/') {
          returnPath = '/';
        }
        const returnUrl = returnPath + '?country=' + newCountryCode;
        
        window.location.href = returnUrl;
        
      });
    });

    // Currency selection
    const currencyOptions = document.querySelectorAll('#currency-sidebar .footer-sidebar__option input[type="radio"]');
    currencyOptions.forEach(option => {
      option.addEventListener('change', (e) => {
        if (e.target.checked) {
          // Update active state
          this.updateActiveOption(e.target, 'currency');
          
          // Submit form
          setTimeout(() => {
            e.target.closest('form').submit();
          }, 150);
        }
      });
    });

    // Language selection
    const languageOptions = document.querySelectorAll('#language-sidebar .footer-sidebar__option input[type="radio"]');
    languageOptions.forEach(option => {
      option.addEventListener('change', (e) => {
        if (e.target.checked) {
          // Update active state
          this.updateActiveOption(e.target, 'language');
          
          // Submit form
          setTimeout(() => {
            e.target.closest('form').submit();
          }, 150);
        }
      });
    });
  }

  updateActiveOption(selectedInput, type) {
    const sidebar = document.querySelector(`#${type}-sidebar`);
    if (sidebar) {
      // Remove active class from all options
      const allOptions = sidebar.querySelectorAll('.footer-sidebar__option');
      allOptions.forEach(opt => opt.classList.remove('active'));
      
      // Add active class to selected option
      const selectedOption = selectedInput.closest('.footer-sidebar__option');
      if (selectedOption) {
        selectedOption.classList.add('active');
      }
    }
  }

  trapFocus(element) {
    const focusableElements = element.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements[focusableElements.length - 1];

    if (firstFocusableElement) {
      firstFocusableElement.focus();
    }

    element.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstFocusableElement) {
            lastFocusableElement.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastFocusableElement) {
            firstFocusableElement.focus();
            e.preventDefault();
          }
        }
      }
    });
  }

  setupSearch() {
    // Country search
    const countrySearchInput = document.getElementById('country-filter-input-sidebar');
    if (countrySearchInput) {
      countrySearchInput.addEventListener('input', (e) => {
        const searchTerm = e.target.value.toLowerCase().trim();
        const countryOptions = document.querySelectorAll('#country-sidebar .footer-sidebar__option[data-country]');
        
        countryOptions.forEach(option => {
          const countryName = option.dataset.country;
          const countryText = option.querySelector('.footer-sidebar__option-text').textContent.toLowerCase();
          
          if (searchTerm === '' || countryName.includes(searchTerm) || countryText.includes(searchTerm)) {
            option.classList.remove('hidden');
          } else {
            option.classList.add('hidden');
          }
        });

        // Show/hide section titles based on visible items
        this.toggleSectionVisibility('footer-sidebar__popular-countries');
        this.toggleSectionVisibility('footer-sidebar__all-countries');
      });
    }

    // Currency search
    const currencySearchInput = document.getElementById('currency-filter-input-sidebar');
    if (currencySearchInput) {
      currencySearchInput.addEventListener('input', (e) => {
        const searchTerm = e.target.value.toLowerCase().trim();
        const currencyOptions = document.querySelectorAll('#currency-sidebar .footer-sidebar__option[data-currency]');
        
        currencyOptions.forEach(option => {
          const currencyName = option.dataset.currency;
          const currencyText = option.querySelector('.footer-sidebar__option-text').textContent.toLowerCase();
          const currencyCode = option.querySelector('.footer-sidebar__option-currency').textContent.toLowerCase();
          
          if (searchTerm === '' || 
              currencyName.includes(searchTerm) || 
              currencyText.includes(searchTerm) || 
              currencyCode.includes(searchTerm)) {
            option.classList.remove('hidden');
          } else {
            option.classList.add('hidden');
          }
        });
      });
    }
  }

  toggleSectionVisibility(sectionClass) {
    const section = document.querySelector(`.${sectionClass}`);
    if (!section) return;

    const visibleOptions = section.querySelectorAll('.footer-sidebar__option:not(.hidden)');
    
    if (visibleOptions.length === 0) {
      section.style.display = 'none';
    } else {
      section.style.display = 'block';
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new FooterSidebar();
});

// Also initialize immediately if DOM is already loaded
if (document.readyState !== 'loading') {
  new FooterSidebar();
}
